import React from "react";
import { Download, FilterX } from "lucide-react";
import ReactPaginationStyle from "src/components/ReactPagination";
import Button from "src/components/custom/Button";
import { useTranslation } from "react-i18next";
import SearchableSelect from "src/components/custom/SearchableSelect";

const GenericDataLayout = ({
  title,
  subtitle,
  itiName,
  actions = [],
  filters = [],
  onResetFilters,
  onDownload,
  pagination,
  children,
  isDownloading = false,
  isLoading = false,
}) => {
  const { t } = useTranslation();

  const renderFilterInput = (filter, index) => {
    // Shared classes: h-[36px] ensures exact height match with SearchableSelect
    const baseInputClass =
      "w-full text-sm border border-gray-200 rounded-lg focus:ring-1 focus:ring-primary/30 focus:border-primary outline-none transition-all bg-white hover:border-gray-300 h-[36px]";
    switch (filter.type) {
      case "date":
        return (
          <div key={index} className="w-full">
            <label className="text-xs font-semibold text-primary mb-1.5 block">
              {filter.label}
            </label>
            <div className="relative">
              <input
                type="date"
                value={filter.value}
                onChange={(e) => filter.onChange(e.target.value)}
                onClick={(e) => e.target.showPicker && e.target.showPicker()}
                min={filter.min}
                max={filter.max}
                className={`${baseInputClass} px-3 py-1.5 text-gray-700 cursor-pointer focus:border-primary`}
              />
            </div>
          </div>
        );
      case "select":
        return (
          <div key={index} className="w-full">
            <SearchableSelect
              label={filter.label}
              placeholder={`Select ${filter.label}`}
              options={[{ label: t("trainer.all"), value: "all" }, ...filter.options]}
              value={filter.value}
              onChange={(newValue) => filter.onChange(newValue)}
              disabled={filter.disabled}
            />
          </div>
        );
      case "custom":
        return (
          <div key={index} className="w-full">
            {typeof filter.render === "function" ? filter.render() : null}
          </div>
        );
      default:
        return null;
    }
  };
  return (
    <div className="container px-4 mx-auto my-6 min-h-screen bg-gray-50/50 pb-10">
      {/* --- Header Section --- */}
      <div className="mb-6">
        <div className="flex flex-col md:flex-row md:items-end justify-between gap-4">
          <div>
            <h1 className="text-2xl font-bold text-primary tracking-tight leading-tight pb-2">
              {title}
            </h1>
            {itiName && (
              <div className="font-bold text-primary tracking-tight leading-tight mb-2" style={{ fontSize: '21px' }}>
                {itiName}
              </div>
            )}
            {subtitle && (
              <div className="font-bold text-primary tracking-tight leading-tight mt-0">
                {subtitle}
              </div>
            )}
          </div>

          <div className="flex flex-wrap items-center gap-3">
            {actions.map((action, idx) => (
              <a
                key={idx}
                href={action.href || "#"}
                target={action.target || "_self"}
                onClick={action.onClick}
                className={
                  action.variant === "solid"
                    ? "inline-flex items-center gap-2 px-4 py-2 text-sm font-semibold text-white bg-primary border border-primary rounded-lg hover:bg-primary/90 transition-colors cursor-pointer shadow-sm"
                    : "inline-flex items-center gap-2 px-3 py-2 text-sm font-medium text-primary border border-primary/30 rounded-lg bg-white hover:bg-primary/5 transition-colors cursor-pointer"
                }
              >
                {action.icon && <action.icon size={14} />}
                {action.label}
              </a>
            ))}

            {onDownload && (
              <Button
                disabled={pagination.totalRecords === 0 || isDownloading}
                onClick={onDownload}
                title={
                  <span className="flex items-center gap-2">
                    <Download size={16} />
                    {t("traineeDetails.download")}
                  </span>
                }
                className={`px-4 py-2 text-sm font-medium text-white rounded-lg shadow-sm transition-all
                  ${
                    pagination.totalRecords === 0 || isDownloading
                      ? "!bg-gray-400 !cursor-not-allowed !shadow-none"
                      : "bg-primary hover:bg-primary/90 hover:shadow-md"
                  }`}
                type="button"
              />
            )}
          </div>
        </div>
      </div>

      {/* --- Main Card --- */}
      <div className="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden">
        {/* --- Filter Area --- */}
        <div className="p-5 border-b border-gray-100 bg-gray-50/30">
          {/* Flex Container: Filters (Left) --- Button (Right) */}
          <div className="flex flex-col lg:flex-row gap-4 lg:items-end justify-between">
            {/* 1. Filters Grid (Takes available space) */}
            <div className="flex-1 w-full grid grid-cols-1 sm:grid-cols-2 md:grid-cols-4 xl:grid-cols-6 gap-4">
              {filters.map((filter, index) => renderFilterInput(filter, index))}
            </div>

            {/* 2. Reset Button (Anchored Right) */}
            {filters.length > 0 && (
              <div className="flex-none pt-2 lg:pt-0">
                <button
                  onClick={onResetFilters}
                  className="sm:w-auto px-4 py-2 text-sm font-medium text-primary bg-white border border-primary/30 rounded-lg hover:bg-primary/5 transition-colors flex items-center justify-center gap-2 h-[38px] whitespace-nowrap shadow-sm"
                  title="Reset Filters"
                >
                  <FilterX size={15} />
                  {t("trainer.clearFilters")}
                </button>
              </div>
            )}
          </div>
        </div>

        {/* --- Table Content Area --- */}
        <div className="relative min-h-[300px]">
          {isLoading ? (
            <div className="absolute inset-0 flex items-center justify-center bg-white/80 z-10">
              <span className="text-primary font-medium animate-pulse">
                Loading data...
              </span>
            </div>
          ) : null}

          {children}

          {!isLoading && pagination.totalRecords === 0 && (
            <div className="absolute inset-0 flex flex-col items-center justify-center">
              <p className="text-primary/70 font-medium">
                No records found.
              </p>
            </div>
          )}
        </div>

        {/* --- Footer / Pagination --- */}
        {pagination.totalRecords > 0 && <div className="p-4 border-t border-gray-100 bg-gray-50 flex flex-col md:flex-row justify-between items-center gap-4">
          <div className="flex items-center gap-4">
            <div className="flex items-center gap-2">
              <span className="text-xs font-semibold text-primary">Rows:</span>
              <select
                onChange={(e) =>
                  pagination.onLimitChange(Number(e.target.value))
                }
                value={pagination.limit}
                className="px-2 py-1 text-sm border border-gray-300 rounded focus:ring-1 focus:ring-primary focus:border-primary outline-none bg-white"
              >
                {[10, 25, 50, 100].map((size) => (
                  <option key={size} value={size}>
                    {size}
                  </option>
                ))}
              </select>
            </div>
            <div className="h-4 w-px bg-gray-300 hidden sm:block"></div>
            <span className="text-sm text-primary">
              Showing{" "}
              <span className="font-bold">
                {(pagination.page - 1) * pagination.limit + 1}
              </span>{" "}
              -{" "}
              <span className="font-bold">
                {Math.min(
                  pagination.page * pagination.limit,
                  pagination.totalRecords || 0
                )}
              </span>{" "}
              of{" "}
              <span className="font-bold">{pagination.totalRecords || 0}</span>
            </span>
          </div>

          <div className="flex justify-end">
            <ReactPaginationStyle
              total={pagination.totalRecords}
              currentPage={pagination.page}
              handlePagination={(p) => pagination.onPageChange(p?.selected + 1)}
              limit={pagination.limit}
            />
          </div>
        </div>}
      </div>
    </div>
  );
};

export default GenericDataLayout;